type LogLevel = 'debug' | 'info' | 'warn' | 'error'

const COLORS: Record<string, string> = {
  webchat: '#9C27B0',
  client: '#2196F3',
  store: '#4CAF50',
  events: '#00BCD4',
}

const LEVEL_COLORS: Record<LogLevel, string> = {
  debug: '#9E9E9E',
  info: '#2196F3',
  warn: '#FF9800',
  error: '#F44336',
}

let _enabled = false
let _trackFn: ((event: string, properties?: Record<string, unknown>) => void) | null = null
let _startTime = Date.now()

export const setDebugEnabled = (enabled: boolean) => {
  _enabled = enabled
  _startTime = Date.now()
}

export const setTrackFn = (fn: typeof _trackFn) => {
  _trackFn = fn
}

export const isDebugEnabled = () => _enabled

const formatTime = () => {
  const elapsed = Date.now() - _startTime
  const seconds = Math.floor(elapsed / 1000)
  const ms = elapsed % 1000
  return `+${seconds}.${ms.toString().padStart(3, '0')}s`
}

const formatValue = (val: unknown): unknown => {
  if (val === undefined) return 'undefined'
  if (val === null) return 'null'
  if (val instanceof Error) return { error: val.message, stack: val.stack }
  return val
}

const createLogger = (name: string) => {
  const color = COLORS[name] || '#666'

  const log = (level: LogLevel, ...args: unknown[]) => {
    if (!_enabled && level !== 'warn' && level !== 'error') return

    const time = formatTime()
    const prefix = `%c[bp:${name}]%c ${time} %c${level.toUpperCase()}%c`
    const styles = [
      `color: ${color}; font-weight: bold;`,
      'color: #999; font-size: 10px;',
      `color: ${LEVEL_COLORS[level]}; font-weight: bold;`,
      'color: inherit;',
    ]

    const formattedArgs = args.map(formatValue)

    // eslint-disable-next-line no-console
    console[level](prefix, ...styles, ...formattedArgs)

    // Send to analytics if configured
    if (_trackFn) {
      const [message, properties] = args
      _trackFn(`debug:${name}:${level}`, {
        message: typeof message === 'string' ? message : undefined,
        ...(typeof properties === 'object' && properties !== null ? properties : {}),
      })
    }
  }

  return {
    debug: (...args: unknown[]) => log('debug', ...args),
    info: (...args: unknown[]) => log('info', ...args),
    warn: (...args: unknown[]) => log('warn', ...args),
    error: (...args: unknown[]) => log('error', ...args),
  }
}

export const debug = {
  webchat: createLogger('webchat'),
  client: createLogger('client'),
  store: createLogger('store'),
  events: createLogger('events'),
  create: createLogger,
}
